import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { io } from "socket.io-client";

const API_BASE = process.env.REACT_APP_API_BASE;
const socket = io(API_BASE, { transports: ["websocket"] });

export default function WorkerSupportChat() {
  const workerId = localStorage.getItem("workerId");

  const [workerEmail, setWorkerEmail] = useState(null);
  const [workerName, setWorkerName] = useState("");
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [sessionClosed, setSessionClosed] = useState(false);

  const chatRef = useRef(null);
  const scrollBottom = () =>
    setTimeout(() => {
      if (chatRef.current)
        chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }, 200);

  /* ------------------------------------------------------
     FETCH WORKER PROFILE
  ------------------------------------------------------ */
  const fetchWorker = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/worker/profile/${workerId}`);
      setWorkerEmail(res.data.email);
      setWorkerName(res.data.name || "Worker");
    } catch {}
  };

  /* ------------------------------------------------------
     START SESSION
  ------------------------------------------------------ */
  const startSession = async (email) => {
    try {
      await axios.post(`${API_BASE}/api/support/start-session`, {
        email,
        name: workerName,
      });
    } catch {}
  };

  /* ------------------------------------------------------
     LOAD MESSAGES
  ------------------------------------------------------ */
  const loadMessages = async (email) => {
    try {
      const res = await axios.get(`${API_BASE}/api/support/livechat/${email}`);
      setMessages(res.data.messages || []);
      scrollBottom();
    } catch {}
  };

  /* ------------------------------------------------------
     END SESSION
  ------------------------------------------------------ */
  const endChat = async () => {
    if (!workerEmail) return;

    try {
      await axios.post(`${API_BASE}/api/support/end-session`, {
        email: workerEmail,
        closedByAdmin: false, // worker side — closing for themselves
      });

      socket.emit("support:endSession", { email: workerEmail });

      setSessionClosed(true);
    } catch (err) {
      console.error("❌ End chat failed", err);
    }
  };

  /* ------------------------------------------------------
     SOCKET + SETUP
  ------------------------------------------------------ */
  useEffect(() => {
    fetchWorker();
  }, []);

  useEffect(() => {
    if (!workerEmail) return;

    startSession(workerEmail);
    loadMessages(workerEmail);

    socket.emit("joinSupportRoom", { email: workerEmail });

    socket.on("support:message", (msg) => {
      if (msg.email === workerEmail) {
        setMessages((prev) => [...prev, msg]);
        scrollBottom();
      }
    });

    socket.on("support:ended", () => {
      setSessionClosed(true);
    });

    return () => {
      socket.off("support:message");
      socket.off("support:ended");
    };
  }, [workerEmail]);

  /* ------------------------------------------------------
     SEND MESSAGE
  ------------------------------------------------------ */
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || sessionClosed) return;

    const msgObj = {
      email: workerEmail,
      sender: workerEmail,
      text: newMsg,
      time: new Date(),
    };

    // show instantly
    setMessages((prev) => [...prev, msgObj]);
    setNewMsg("");
    scrollBottom();

    try {
      await axios.post(`${API_BASE}/api/support/livechat/send`, msgObj);
      socket.emit("support:message", msgObj);
    } catch {}
  };

  /* ------------------------------------------------------
     UI (IDENTICAL TO CUSTOMER)
  ------------------------------------------------------ */
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* HEADER */}
      <div className="bg-white shadow p-5 flex justify-between items-center font-bold text-2xl text-blue-700 max-w-3xl mx-auto w-full">
        <span>💬 Worker Support Chat</span>

        {!sessionClosed && workerEmail && (
          <button
            onClick={endChat}
            className="text-red-500 hover:underline text-sm"
          >
            End Chat
          </button>
        )}
      </div>

      {/* CHAT CARD */}
      <div className="max-w-3xl mx-auto mt-6 w-full bg-white rounded-3xl shadow-lg flex flex-col flex-1 overflow-hidden border border-gray-200">

        {/* Chat ended message */}
        {sessionClosed && (
          <div className="text-center text-red-500 py-4 font-semibold">
            This chat session has ended.
          </div>
        )}

        {/* Chat messages */}
        <div ref={chatRef} className="flex-1 p-6 overflow-y-auto space-y-4">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${
                msg.sender === workerEmail ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`px-4 py-2 rounded-2xl shadow text-sm max-w-xs ${
                  msg.sender === workerEmail
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-900"
                }`}
              >
                {msg.text}
              </div>
            </div>
          ))}
        </div>

        {/* INPUT BAR */}
        {!sessionClosed && (
          <form
            onSubmit={sendMessage}
            className="p-4 bg-gray-100 flex gap-3 border-t"
          >
            <input
              type="text"
              value={newMsg}
              onChange={(e) => setNewMsg(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 border border-gray-300 px-4 py-3 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-semibold"
            >
              Send
            </button>
          </form>
        )}
      </div>

      <div className="text-center text-gray-500 text-sm py-4">
        Monday–Friday | 9 AM – 5 PM (Pacific Time)
      </div>
    </div>
  );
}
